import { Node, mergeAttributes } from '@tiptap/core';
import { isCustomVariable } from '../utils/variable-utils';

export interface VariableNodeOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    variableNode: {
      insertVariable: (name: string) => ReturnType;
    };
  }
}

export const VariableNode = Node.create<VariableNodeOptions>({
  name: 'variable',

  group: 'inline',

  inline: true,

  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      name: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-variable'),
        renderHTML: (attributes) => ({
          'data-variable': attributes.name,
        }),
      },
      custom: {
        default: false,
        // Aceita data-custom="true" no HTML importado e também infere pela chave
        // (modelos antigos não têm o atributo).
        parseHTML: (element) => {
          const attr = element.getAttribute('data-custom');
          if (attr === 'true') return true;
          if (attr === 'false') return false;
          const name = element.getAttribute('data-variable') ?? '';
          return isCustomVariable(name);
        },
        renderHTML: (attributes) =>
          attributes.custom ? { 'data-custom': 'true' } : {},
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-variable]',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const isCustom = node.attrs.custom === true;
    const classes = isCustom ? 'variable-chip variable-chip--custom' : 'variable-chip';
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-variable': node.attrs.name,
        ...(isCustom ? { 'data-custom': 'true' } : {}),
        class: classes,
        contenteditable: 'false',
      }),
      `{${node.attrs.name}}`,
    ];
  },

  addCommands() {
    return {
      insertVariable:
        (name: string) =>
        ({ chain }) => {
          return chain()
            .focus()
            .insertContent({
              type: this.name,
              attrs: { name, custom: isCustomVariable(name) },
            })
            .run();
        },
    };
  },
});
